import { createSlice } from '../factory';
import { getChatListApi, getChatDetailApi, getScheduledUnits, createChat, markMessagesRead, getNotificationCountApi, } from '@repo/api';
import { MESSAGE_STATUS, } from '@repo/utils';
import { chatTourFeedbackInitialState, createChatTourFeedbackActions, } from './chat-tour-feedback-queue';
const initialState = {
    unreadMessages: 0,
    chatList: [],
    chatDetail: [],
    scheduledUnits: [],
    chatDetailData: null,
    chatListData: null,
    isPolling: false,
    wsStatus: 'disconnected',
    wsConnectedChatId: null,
    isFetchingChatList: false,
    ...chatTourFeedbackInitialState,
};
export const chatSlice = createSlice({
    name: 'chat',
    initialState,
    persist: {
        keys: [], // Don't persist chat data - it should be fetched fresh
    },
    actions: ({ setSlice, getSlice, get }) => {
        const tourFeedbackActions = createChatTourFeedbackActions({
            getSlice: () => getSlice(),
            setSlice,
        });
        return {
            clearChatData: () => {
                setSlice(initialState);
            },
            resetUnreadMessages: () => {
                setSlice({ unreadMessages: 0 });
            },
            setUnreadMessages: (count) => {
                setSlice({ unreadMessages: Number.isFinite(count) ? count : 0 });
            },
            resetChatDetail: () => {
                setSlice({
                    chatDetailData: null,
                    chatDetail: [],
                    isPolling: false,
                });
            },
            removeChatMessageById: (messageId, options) => {
                const current = getSlice();
                const remaining = current.chatDetail.filter((msg) => msg.id !== messageId);
                // Nothing removed — a repeated delete frame, or a message from a page
                // that was never loaded. Returning keeps the count from drifting.
                if (remaining.length === current.chatDetail.length)
                    return;
                // Pagination asks for another page whenever the api's total exceeds the
                // messages on hand, so a total left one too high makes an already
                // complete list refetch on the next scroll. Only messages the api
                // counted may adjust it: an optimistic message dropped on retry never
                // made it into the total, and decrementing for it would instead hide a
                // page that really does exist.
                const detail = current.chatDetailData;
                const adjustTotal = options?.adjustTotalCount === true && !!detail?.chatMessages;
                setSlice({
                    chatDetail: remaining,
                    ...(adjustTotal && detail
                        ? {
                            chatDetailData: {
                                ...detail,
                                chatMessages: {
                                    ...detail.chatMessages,
                                    count: Math.max(0, (detail.chatMessages.count ?? 0) - 1),
                                },
                            },
                        }
                        : {}),
                });
            },
            setChatDetail: (payload) => {
                const { append = false, messages } = payload;
                const current = getSlice();
                if (append) {
                    const existingMessagesMap = new Map(current.chatDetail.map((msg) => [msg?.id, msg]));
                    // Insert or replace new messages
                    messages.forEach((msg) => {
                        existingMessagesMap.set(msg?.id, msg);
                    });
                    // Convert Map back to array and sort by creation date
                    const allMessages = Array.from(existingMessagesMap.values());
                    const sortedMessages = allMessages.sort((a, b) => new Date(b.atCreated).getTime() - new Date(a.atCreated).getTime());
                    setSlice({ chatDetail: sortedMessages });
                }
                else {
                    setSlice({ chatDetail: messages });
                }
            },
            replaceMessage: (payload) => {
                const current = getSlice();
                const index = current.chatDetail.findIndex((m) => m.id === payload.tempId);
                if (index !== -1) {
                    const updated = [...current.chatDetail];
                    updated[index] = payload.actual;
                    setSlice({ chatDetail: updated });
                }
            },
            markMessageAsFailed: (messageId) => {
                const current = getSlice();
                const updated = current.chatDetail.map((msg) => msg.id === messageId ? { ...msg, status: MESSAGE_STATUS.FAILED } : msg);
                setSlice({ chatDetail: updated });
            },
            setPollingStatus: (isPolling) => {
                setSlice({ isPolling });
            },
            setWebSocketStatus: (status) => {
                setSlice({ wsStatus: status });
            },
            setWebSocketConnectedChat: (chatId) => {
                setSlice({ wsConnectedChatId: chatId });
            },
            /**
             * Optimistically mark a chat as read in local state.
             * (Backend `markMessagesRead` doesn't always immediately reflect in list payloads.)
             */
            markChatAsReadLocal: (chatId) => {
                const current = getSlice();
                const existing = current.chatList.find((c) => c.id === chatId);
                const wasUnread = !!existing?.hasUnreadMessages;
                const updatedChatList = current.chatList.map((c) => c.id === chatId ? { ...c, hasUnreadMessages: false } : c);
                setSlice({
                    chatList: updatedChatList,
                    chatListData: current.chatListData
                        ? { ...current.chatListData, results: updatedChatList }
                        : current.chatListData,
                    unreadMessages: wasUnread
                        ? Math.max(0, (current.unreadMessages || 0) - 1)
                        : current.unreadMessages,
                });
            },
            // Async actions
            fetchChatList: async (page = 1) => {
                try {
                    setSlice({ isFetchingChatList: true });
                    const response = await getChatListApi(page, 10);
                    const current = getSlice();
                    setSlice({
                        chatListData: response,
                        chatList: page > 1 ? [...current.chatList, ...response.results] : response.results,
                        isFetchingChatList: false,
                    });
                }
                catch (error) {
                    console.error('Error fetching chat list:', error);
                    setSlice({ isFetchingChatList: false });
                }
            },
            fetchChatDetail: async (chatId, page = 1) => {
                try {
                    const response = await getChatDetailApi(chatId, page, 10);
                    const current = getSlice();
                    setSlice({
                        chatDetailData: response,
                        chatDetail: page > 1
                            ? [...current.chatDetail, ...response.chatMessages.results]
                            : response.chatMessages.results,
                    });
                }
                catch (error) {
                    console.error('Error fetching chat detail:', error);
                }
            },
            setCurrentUserScheduledUnits: async () => {
                try {
                    const units = await getScheduledUnits();
                    setSlice({ scheduledUnits: units });
                }
                catch (error) {
                    console.error('Error fetching scheduled units:', error);
                    setSlice({ scheduledUnits: [] });
                }
            },
            addScheduledUnit: async (unitSlug, tourType) => {
                try {
                    const response = await createChat(unitSlug, tourType);
                    const { chat } = response;
                    const addedUnit = {
                        id: chat?.id || '',
                        unitId: chat?.unitId || '',
                        unit: {
                            id: chat?.unit?.id || '',
                            slug: chat?.unit?.slug || '',
                            activeListingId: chat?.unit?.activeListing?.id || '',
                        },
                    };
                    const current = getSlice();
                    if (!current.scheduledUnits.some((u) => u.id === addedUnit.id)) {
                        setSlice({
                            scheduledUnits: [...current.scheduledUnits, addedUnit],
                        });
                    }
                    return addedUnit;
                }
                catch (error) {
                    console.error('Error creating chat:', error);
                    return null;
                }
            },
            markMessagesAsRead: async (chatId) => {
                try {
                    await markMessagesRead(chatId);
                }
                catch (error) {
                    console.error('Error marking messages as read:', error);
                }
            },
            fetchNotificationCount: async () => {
                try {
                    const response = await getNotificationCountApi();
                    setSlice({ unreadMessages: response.unreadChats || 0 });
                }
                catch (error) {
                    console.error('Error fetching notification count:', error);
                    // Don't update state on error to preserve last known count
                }
            },
            ...tourFeedbackActions,
        };
    },
});
